import React, { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import ImageResize from 'quill-image-resize-module-react';
import { Buffer } from "buffer";
import { uploadAndGetURL } from "../Utils";


Quill.register('modules/imageResize', ImageResize);

// Whitelist the font sizes you want to use
const Size = Quill.import('attributors/style/size');
const fontSizes = ['10px', '12px', '14px', '16px', '18px', '20px', '22px'];
Size.whitelist = fontSizes;
Quill.register(Size, true);

// Register the alignment module
const AlignStyle = Quill.import('attributors/style/align');
AlignStyle.whitelist = ['left', 'center', 'right', 'justify'];
Quill.register(AlignStyle, true);


if (typeof window !== 'undefined') {
    window.Buffer = window.Buffer || Buffer;
}

const uploadImageToS3 = async (file) => {
    try {
        return await uploadAndGetURL(file, 'image');
    } catch (err) {
        console.error("Upload failed:", err);
        return null;
    }
};

/**
 * @param {Quill} quill The Quill editor instance.
 * @param {any} delta The delta object from the text-change event.
 */
const handlePastedImages = async (quill, delta) => {
    const Delta = Quill.import('delta');

    const base64toFile = async (base64, filename) => {
        const res = await fetch(base64);
        const blob = await res.blob();
        return new File([blob], filename, { type: blob.type });
    };

    const imagePromises = [];
    let retainIndex = 0;

    delta.ops.forEach(op => {
        if (op.insert && op.insert.image && op.insert.image.startsWith('data:image/')) {
            const base64 = op.insert.image;
            const promise = base64toFile(base64, `pasted-image-${Date.now()}.png`)
              .then(file => uploadImageToS3(file))
              .then(imageUrl => {
                  if (imageUrl) {
                      return new Delta()
                        .retain(retainIndex)
                        .delete(1)
                        .insert({ image: imageUrl }, op.attributes);
                  }
                  return new Delta().retain(retainIndex + 1);
              });
            imagePromises.push(promise);
        }
        retainIndex += (op.insert || op.delete || op.retain || '').length || 1;
    });

    if (imagePromises.length > 0) {
        const imageDeltas = await Promise.all(imagePromises);
        const finalDelta = imageDeltas.reduce((acc, current) => acc.compose(current), new Delta());
        quill.updateContents(finalDelta, 'silent');
    }
};


const CustomQuillEditor = forwardRef(
  // 1. ACCEPT THE `placeholder` PROP HERE
  ({ readOnly, initialHtml, onTextChange, onSelectionChange, placeholder }, ref) => {
      const containerRef = useRef(null);
      const onTextChangeRef = useRef(onTextChange);
      const onSelectionChangeRef = useRef(onSelectionChange);
      const isMountedRef = useRef(false);

      useLayoutEffect(() => {
          onTextChangeRef.current = onTextChange;
          onSelectionChangeRef.current = onSelectionChange;
      });

      useEffect(() => {
          if (ref.current) {
              ref.current.enable(!readOnly);
          }
      }, [ref, readOnly]);

      // 3. ADD THIS `useEffect` TO DYNAMICALLY UPDATE THE PLACEHOLDER
      useEffect(() => {
          if (ref.current && ref.current.root) {
              // Quill displays the placeholder using a `data-placeholder` attribute on its root editor element
              ref.current.root.dataset.placeholder = placeholder || '';
          }
      }, [placeholder, ref]); // This effect runs whenever the placeholder prop changes

      useEffect(() => {
          const imageHandler = () => {
              const input = document.createElement('input');
              input.setAttribute('type', 'file');
              input.setAttribute('accept', 'image/*');
              input.click();
              input.onchange = async () => {
                  if (input.files && input.files[0]) {
                      const file = input.files[0];
                      const quill = ref.current;
                      if (!quill) return;
                      const range = quill.getSelection(true);
                      const imageUrl = await uploadImageToS3(file);
                      if (imageUrl) {
                          quill.insertEmbed(range.index, 'image', imageUrl);
                          quill.setSelection(range.index + 1);
                      }
                  }
              };
          };

          const COLORS = [
              "#000000", "#e60000", "#ff9900", "#ffff00",
              "#008a00", "#0066cc", "#9933ff", "#ffffff"
          ];

          const quillModules = {
              toolbar: {
                  container: [
                      [{ 'header': [1, 2, 3, false] }],
                      [{ 'size': [false, ...fontSizes] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'script': 'sub'}, { 'script': 'super' }],
                      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                      [{ 'align': [] }],
                      [{ 'color': COLORS }, { 'background': COLORS }],
                      ['link', 'image', 'video'],
                      ['clean'],
                  ],
                  handlers: { 'image': imageHandler },
              },
              imageResize: { modules: ['Resize', 'DisplaySize'] }
          };

          const container = containerRef.current;
          const editorContainer = container.appendChild(container.ownerDocument.createElement('div'));

          // 2. PASS THE `placeholder` PROP TO THE QUILL CONSTRUCTOR
          const quill = new Quill(editorContainer, {
              theme: 'snow',
              modules: quillModules,
              placeholder: placeholder, // Use the prop for initial setup
          });

          ref.current = quill;

          const addSymbolPicker = () => {
              const symbolCategories = {
                  "Mathematics": [
                      { value: '≠', text: '≠ (Not Equal)' }, { value: '≈', text: '≈ (Almost Equal)' },
                      { value: '≤', text: '≤ (Less than/Equal)' }, { value: '≥', text: '≥ (Greater than/Equal)' },
                      { value: '√', text: '√ (Square Root)' }, { value: '∛', text: '∛ (Cube Root)' },
                      { value: '∞', text: '∞ (Infinity)' }, { value: '∫', text: '∫ (Integral)' },
                      { value: '∂', text: '∂ (Partial Differential)' }, { value: '∑', text: '∑ (Summation)' },
                      { value: '∏', text: '∏ (Product)' }, { value: '∈', text: '∈ (Element of)' },
                  ],
                  "Greek Letters": [
                      { value: 'α', text: 'α (Alpha)' }, { value: 'β', text: 'β (Beta)' },
                      { value: 'γ', text: 'γ (Gamma)' }, { value: 'Δ', text: 'Δ (Delta)' },
                      { value: 'ε', text: 'ε (Epsilon)' }, { value: 'θ', text: 'θ (Theta)' },
                      { value: 'λ', text: 'λ (Lambda)' }, { value: 'μ', text: 'μ (Mu)' },
                      { value: 'π', text: 'π (Pi)' }, { value: 'Σ', text: 'Σ (Sigma)' }, { value: 'Ω', text: 'Ω (Omega)' },
                  ],
                  "Scientific & Units": [
                      { value: '°C', text: '°C (Celsius)' }, { value: '°F', text: '°F (Fahrenheit)' },
                      { value: '°', text: '° (Degree)' }, { value: 'Å', text: 'Å (Angstrom)' },
                      { value: 'µ', text: 'µ (Micro)' }, { value: '→', text: '→ (Vector Arrow)' },
                  ],
                  "General": [
                      { value: '©', text: '© (Copyright)' }, { value: '®', text: '® (Registered)' },
                      { value: '™', text: '™ (Trademark)' },
                  ],
              };

              const toolbar = container.querySelector('.ql-toolbar');
              if (toolbar) {
                  const symbolGroup = document.createElement('span');
                  symbolGroup.className = 'ql-formats';

                  const picker = document.createElement('select');
                  picker.className = 'ql-symbol-picker';

                  const defaultOption = document.createElement('option');
                  defaultOption.setAttribute('selected', 'true');
                  defaultOption.setAttribute('disabled', 'true');
                  defaultOption.innerHTML = 'Σ Symbols';
                  picker.appendChild(defaultOption);

                  Object.keys(symbolCategories).forEach(category => {
                      const optgroup = document.createElement('optgroup');
                      optgroup.label = category;
                      symbolCategories[category].forEach(symbol => {
                          const option = document.createElement('option');
                          option.value = symbol.value;
                          option.textContent = symbol.text;
                          optgroup.appendChild(option);
                      });
                      picker.appendChild(optgroup);
                  });

                  picker.addEventListener('change', () => {
                      const selectedValue = picker.value;
                      if (selectedValue) {
                          quill.focus();
                          const range = quill.getSelection(true);
                          quill.insertText(range.index, selectedValue, 'user');
                          quill.setSelection(range.index + 1);
                          picker.selectedIndex = 0;
                      }
                  });

                  symbolGroup.appendChild(picker);
                  toolbar.appendChild(symbolGroup);
              }
          };

          const injectCss = () => {
              const styleId = 'quill-custom-styles';
              if (document.getElementById(styleId)) return;

              const fontSizeCss = fontSizes.map(size => `
                    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="${size}"]::before,
                    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="${size}"]::before {
                        content: '${size}';
                    }
                `).join('');

              const symbolPickerCss = `
                    .ql-toolbar .ql-symbol-picker {
                        width: 220px;
                        border: 1px solid #ccc;
                        padding: 3px 5px;
                    }
                    .ql-symbol-picker optgroup {
                        font-weight: bold;
                        font-style: normal;
                    }
                `;

              const fullCss = symbolPickerCss + fontSizeCss;

              const style = document.createElement('style');
              style.id = styleId;
              style.type = 'text/css';
              style.appendChild(document.createTextNode(fullCss));
              document.head.appendChild(style);
          };

          injectCss();
          addSymbolPicker();

          if (initialHtml) {
              quill.clipboard.dangerouslyPasteHTML(initialHtml);
          }

          isMountedRef.current = true;

          quill.on(Quill.events.TEXT_CHANGE, function(delta, oldDelta, source) {
              if (isMountedRef.current) {
                  onTextChangeRef.current?.(delta, oldDelta, source);
              }
              const hasPastedImage = delta.ops.some(op => op.insert && op.insert.image && op.insert.image.startsWith('data:image/'));
              if (source === 'user' && hasPastedImage) {
                  handlePastedImages(this, delta);
              }
          });

          quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
              if (isMountedRef.current) {
                  onSelectionChangeRef.current?.(...args);
              }
          });

          quill.root.addEventListener('drop', (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
                  const range = quill.getSelection(true);
                  Array.from(e.dataTransfer.files).forEach(async (file) => {
                      if (file.type.startsWith('image/')) {
                          const imageUrl = await uploadImageToS3(file);
                          if (imageUrl) {
                              quill.insertEmbed(range.index, 'image', imageUrl);
                              quill.setSelection(range.index + 1);
                          }
                      }
                  });
              }
          });

          return () => {
              isMountedRef.current = false;
              ref.current = null;
              if (container) container.innerHTML = '';
          };
      }, [ref]);

      return <div ref={containerRef}></div>;
  },
);

CustomQuillEditor.displayName = 'CustomQuillEditor';
export default CustomQuillEditor;